import { Injectable, computed, signal } from '@angular/core';
import { OnboardingLanding, RfcChallenge, TokenStatus } from './onboarding-session.model';

/**
 * Sesión de onboarding, solo en memoria (plan 6.4). Eso incluye el sufijo del RFC: nunca va a storage.
 * Al recargar se pierde a propósito: el guard la reconstruye desde el `/onboarding/{token}` de la URL
 * y, como `rfcVerified` vuelve a `false`, el cliente tiene que validar el RFC otra vez.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingSessionStore {
  private readonly tokenState = signal<string | null>(null);
  private readonly statusState = signal<TokenStatus | null>(null);
  private readonly landingState = signal<OnboardingLanding | null>(null);
  private readonly rfcChallengeState = signal<RfcChallenge | null>(null);
  private readonly rfcSuffixState = signal<string | null>(null);

  readonly token = this.tokenState.asReadonly();
  readonly status = this.statusState.asReadonly();
  readonly landing = this.landingState.asReadonly();
  readonly rfcChallenge = this.rfcChallengeState.asReadonly();
  /** Es lo que el backend espera en `?rfc=` para las escrituras (subidas y borrados). Solo memoria. */
  readonly rfcSuffix = this.rfcSuffixState.asReadonly();
  /** Una sola fuente de verdad: el RFC está validado si y solo si el backend devolvió la landing. */
  readonly rfcVerified = computed(() => this.landingState() !== null);
  /** Sin intentos el backend rechaza el RFC aunque sea correcto: no tiene sentido volver a mandarlo. */
  readonly rfcLocked = computed(() => this.rfcChallengeState()?.attemptsRemaining === 0);
  /** El cliente validó el RFC y el token sigue en curso: es la sesión que vigilan la inactividad y el polling. */
  readonly sessionActive = computed(() => this.statusState() === 'in-progress' && this.rfcVerified());

  /** Con el mismo token conserva la sesión; con otro distinto la reinicia. */
  start(token: string): void {
    if (token === this.tokenState()) {
      return;
    }
    this.tokenState.set(token);
    this.statusState.set(null);
    this.landingState.set(null);
    this.rfcChallengeState.set(null);
    this.rfcSuffixState.set(null);
  }

  setStatus(status: TokenStatus, rfcChallenge: RfcChallenge | null = null): void {
    this.statusState.set(status);
    this.rfcChallengeState.set(rfcChallenge);
  }

  /** Guarda el sufijo tal cual lo escribió el cliente: el backend lo acepta así y no hace falta el desafío. */
  markRfcVerified(landing: OnboardingLanding, rfcSuffix: string): void {
    this.landingState.set(landing);
    this.rfcSuffixState.set(rfcSuffix);
  }

  /**
   * El backend rechazó el RFC. Si informó los intentos que quedan, manda ese número; si no, se descuenta el que se acaba de gastar.
   * Vive en el store y no en la página para que el conteo sobreviva si el cliente sale y vuelve al paso.
   */
  markRfcMismatch(attemptsRemaining?: number): void {
    this.rfcChallengeState.update(
      (challenge) =>
        challenge && {
          ...challenge,
          attemptsRemaining: attemptsRemaining ?? Math.max(challenge.attemptsRemaining - 1, 0),
        },
    );
  }

  markCompleted(): void {
    this.statusState.set('completed');
  }

  clear(): void {
    this.tokenState.set(null);
    this.statusState.set(null);
    this.landingState.set(null);
    this.rfcChallengeState.set(null);
    this.rfcSuffixState.set(null);
  }
}
